import React, { useMemo, useState } from 'react';
import '../css/jyotish-chart.css';

const PLANET_ABBR = {
  Sun: 'Su',
  Moon: 'Mo',
  Mercury: 'Me',
  Venus: 'Ve',
  Mars: 'Ma',
  Jupiter: 'Ju',
  Saturn: 'Sa',
  Uranus: 'Ur',
  Neptune: 'Ne',
  Pluto: 'Pl',
  Rahu: 'Ra',
  Ketu: 'Ke',
};

const PLANET_ORDER = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn',
  'Rahu', 'Ketu', 'Uranus', 'Neptune', 'Pluto',
];

/** South Indian fixed rashi grid (0=Aries … 11=Pisces), row-major; null = center void */
const SOUTH_RASHI_GRID = [
  [11, 0, 1, 2],
  [10, null, null, 3],
  [9, null, null, 4],
  [8, 7, 6, 5],
];

/** Text centers for North Indian diamond chart (% of box) */
const NORTH_HOUSE_POS = {
  1: { left: '50%', top: '18%' },
  2: { left: '22%', top: '10%' },
  3: { left: '10%', top: '28%' },
  4: { left: '22%', top: '50%' },
  5: { left: '10%', top: '72%' },
  6: { left: '22%', top: '88%' },
  7: { left: '50%', top: '80%' },
  8: { left: '78%', top: '88%' },
  9: { left: '90%', top: '72%' },
  10: { left: '78%', top: '50%' },
  11: { left: '90%', top: '28%' },
  12: { left: '78%', top: '10%' },
};

function formatPlanetLabel(key, planet) {
  const abbr = PLANET_ABBR[key] || key.slice(0, 2);
  let label = abbr;
  if (planet?.isRetrograde) label = `(${label})`;
  if (planet?.dignity === 'exalted') label += '↑';
  if (planet?.dignity === 'debilitated') label += '↓';
  return label;
}

function buildChartData(kundli) {
  const houses = Array.from({ length: 12 }, (_, i) => {
    const h = kundli?.houses?.[i];
    return {
      number: h?.number ?? i + 1,
      rashi: typeof h?.rashi === 'number' ? h.rashi : ((kundli?.ascendant?.rashi ?? 0) + i) % 12,
      planets: Array.isArray(h?.planets) ? h.planets : [],
    };
  });

  const byRashi = Array.from({ length: 12 }, () => []);
  PLANET_ORDER.forEach((key) => {
    const p = kundli?.planets?.[key];
    if (!p || p.rashi == null) return;
    byRashi[p.rashi].push({ key, ...p });
  });

  return {
    houses,
    byRashi,
    ascRashi: kundli?.ascendant?.rashi ?? 0,
  };
}

function PlanetMarks({ items, showAsc }) {
  return (
    <div className="jyotish-chart-marks">
      {showAsc && <span className="jyotish-chart-asc">As</span>}
      {items.map((p) => (
        <span
          key={p.key}
          className={`jyotish-chart-planet${p.isRetrograde ? ' is-retro' : ''}${p.dignity === 'exalted' ? ' is-exalted' : ''}${p.dignity === 'debilitated' ? ' is-debil' : ''}`}
        >
          {formatPlanetLabel(p.key, p)}
        </span>
      ))}
    </div>
  );
}

function NorthIndianChart({ data, planets }) {
  return (
    <div className="jyotish-chart-north" aria-label="North Indian chart">
      <svg className="jyotish-chart-north-grid" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet">
        <rect x="1" y="1" width="398" height="398" className="jyotish-chart-frame" />
        <line x1="0" y1="0" x2="400" y2="400" className="jyotish-chart-line" />
        <line x1="400" y1="0" x2="0" y2="400" className="jyotish-chart-line" />
        <polygon points="200,0 0,200 200,400 400,200" className="jyotish-chart-diamond" />
      </svg>
      {data.houses.map((house) => {
        const pos = NORTH_HOUSE_POS[house.number];
        const items = (house.planets || [])
          .map((key) => ({ key, ...(planets?.[key] || {}) }))
          .filter((p) => PLANET_ABBR[p.key]);
        items.sort((a, b) => PLANET_ORDER.indexOf(a.key) - PLANET_ORDER.indexOf(b.key));
        return (
          <div
            key={house.number}
            className={`jyotish-chart-cell jyotish-north-h${house.number}`}
            style={{ left: pos.left, top: pos.top }}
          >
            <span className="jyotish-chart-rashi-num">{house.rashi + 1}</span>
            <PlanetMarks items={items} showAsc={house.number === 1} />
          </div>
        );
      })}
    </div>
  );
}

function SouthIndianChart({ data }) {
  return (
    <div className="jyotish-chart-south" aria-label="South Indian chart">
      {SOUTH_RASHI_GRID.flatMap((row, rowIdx) =>
        row.map((rashi, colIdx) => {
          if (rashi == null) {
            return rowIdx === 1 && colIdx === 1
              ? <div key="center" className="jyotish-chart-south-center" />
              : null;
          }
          const items = (data.byRashi[rashi] || []).slice().sort(
            (a, b) => PLANET_ORDER.indexOf(a.key) - PLANET_ORDER.indexOf(b.key)
          );
          const isAsc = rashi === data.ascRashi;
          return (
            <div
              key={`r${rashi}`}
              className={`jyotish-chart-south-cell${isAsc ? ' is-lagna' : ''}`}
              style={{ gridColumn: colIdx + 1, gridRow: rowIdx + 1 }}
            >
              <span className="jyotish-chart-rashi-num">{rashi + 1}</span>
              <PlanetMarks items={items} showAsc={isAsc} />
            </div>
          );
        })
      )}
    </div>
  );
}

/**
 * Vedic chart with North / South Indian layout toggle
 * and whole_sign / equal_house house-system toggle.
 */
export function JyotishChart({
  kundli,
  houseSystem = 'whole_sign',
  onHouseSystemChange,
  labels = {},
}) {
  const [style, setStyle] = useState('north');
  const data = useMemo(() => buildChartData(kundli), [kundli]);

  if (!kundli) return null;

  return (
    <div className="x_info_w jyotish-chart">


      <div className="jyotish-chart-toggles">
        <div className="jyotish-chart-toggle" role="tablist" aria-label="Chart style">
          <button
            type="button"
            role="tab"
            aria-selected={style === 'north'}
            className={style === 'north' ? 'active' : ''}
            onClick={() => setStyle('north')}
          >
            {labels.north || 'North'}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={style === 'south'}
            className={style === 'south' ? 'active' : ''}
            onClick={() => setStyle('south')}
          >
            {labels.south || 'South'}
          </button>
        </div>
        {typeof onHouseSystemChange === 'function' && (
          <div className="jyotish-chart-toggle" role="tablist" aria-label="House system">
            <button
              type="button"
              role="tab"
              aria-selected={houseSystem === 'whole_sign'}
              className={houseSystem === 'whole_sign' ? 'active' : ''}
              onClick={() => onHouseSystemChange('whole_sign')}
            >
              {labels.wholeSign || 'Whole Sign'}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={houseSystem === 'equal_house'}
              className={houseSystem === 'equal_house' ? 'active' : ''}
              onClick={() => onHouseSystemChange('equal_house')}
            >
              {labels.equalHouse || 'Equal House'}
            </button>
          </div>
        )}
      </div>

      {style === 'north' ? (
        <NorthIndianChart data={data} planets={kundli.planets} />
      ) : (
        <SouthIndianChart data={data} />
      )}

    </div>
  );
}
